import destr from '../utils/destr'
import KY, { Options } from 'ky'
import { vsingleton } from '@niknevis/vue-modules'
import qs from 'qs'

const debug = require('debug')('http')

@vsingleton()
export default class HTTP {
  readonly _defaults: any
  readonly _ky: typeof KY

  constructor() {
    this._defaults = {
      timeout: false,
      headers: {
        // 'Access-Control-Allow-Origin': true,
        // 'Access-Control-Allow-Headers': '*',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      hooks: {}
    }

    this._ky = KY

    if (debug.enabled) {
      setupDebugInterceptor(this)
    }
  }

  private _call(method: string, url, arg1, arg2?): Promise<Response> {
    const hasBody = ['post', 'put', 'patch'].includes(method)
    let options

    if (!hasBody) {
      options = arg1
    } else {
      options = arg2 || {}
      if (arg1 !== undefined) {
        if (arg1.constructor === Object || Array.isArray(arg1)) {
          options.json = arg1
        } else {
          options.body = arg1
        }
      }
    }

    const _options = { ...this._defaults, ...options }

    if (/^https?/.test(url)) {
      delete _options.prefixUrl
    } else if (_options.prefixUrl && typeof url === 'string' && url.startsWith('/')) {
      // Prevents `ky` from throwing "`input` must not begin with a slash when using `prefixUrl`"
      url = url.substr(1)
    }

    if (_options.searchParams) {
      _options.searchParams = qs
        .stringify(
          _options.searchParams,
          {
            encoder: str => {
              if (typeof str === 'boolean') {
                return str ? 1 : 0
              }
              return str === 'true'
                ? 1
                : str === 'false'
                  ? 0
                  : str
            }
          }
        )
    }

    return (async () => {
      try {
        const response = await this._ky[method](url, _options)
        return response
      } catch (error) {
        // Try to fill error with useful data
        if (error.response) {
          error.statusCode = error.response.status
          try {
            const text = await error.response.text()
            error.response.text = () => Promise.resolve(text)
            const json = destr(text)
            error.response.json = () => Promise.resolve(json)
            error.response.data = json
          } catch (_) {
          }
        }

        // Call onError hook
        if (_options.hooks.onError) {
          for (const fn of _options.hooks.onError) {
            const res = fn(error)
            if (res !== undefined) {
              return res
            }
          }
        }

        // Throw error
        throw error
      }
    })()
  }

  setBaseURL(baseURL) {
    this._defaults.prefixUrl = baseURL
  }

  setHeader(name, value) {
    if (!value) {
      delete this._defaults.headers[name]
    } else {
      this._defaults.headers[name] = value
    }
  }

  setToken(token, type) {
    const value = !token ? null : (type ? type + ' ' : '') + token
    this.setHeader('Authorization', value)
  }

  _hook(name, fn) {
    if (!this._defaults.hooks[name]) {
      this._defaults.hooks[name] = []
    }
    this._defaults.hooks[name].push(fn)
  }

  onRequest(fn) {
    this._hook('beforeRequest', fn)
  }

  onRetry(fn) {
    this._hook('beforeRetry', fn)
  }

  onResponse(fn) {
    this._hook('afterResponse', fn)
  }

  onError(fn) {
    this._hook('onError', fn)
  }

  get(url: string, options?: Options) {
    return this._call('get', url, options)
  }

  head(url: string, options?: Options) {
    return this._call('head', url, options)
  }

  delete(url: string, options?: Options) {
    return this._call('delete', url, options)
  }

  post(url: string, data?: any, options?: Options) {
    return this._call('post', url, data, options)
  }

  put(url: string, data?: any, options?: Options) {
    return this._call('put', url, data, options)
  }

  patch(url: string, data?: any, options?: Options) {
    return this._call('patch', url, data, options)
  }
}

const log = (level, ...messages) => console[level]('[Http]', ...messages)

const setupDebugInterceptor = (http: HTTP) => {
  // request
  http.onRequest(req => {
    log(
      'info',
      'Request:',
      '[' + req.method.toUpperCase() + ']',
      req.url
    )

  })

  // response
  http.onResponse((req, options, res) => {
    log(
      'info',
      'Response:',
      '[' + (res.status + ' ' + res.statusText) + ']',
      '[' + req.method.toUpperCase() + ']',
      res.url
    )

    return res
  })

  // error
  http.onError(error => {
    log('error', 'Error:', error)
  })
}
